// PaidGate.jsx
import React from 'react';
import { Lock } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useTheme } from '@/managers/ThemeProvider';
import { motion } from 'framer-motion';
import { authManager, TIERS } from '@/managers/AuthManager'; // Use the single source of truth

// Defines the power level of each tier. Higher is better.
const TIER_HIERARCHY = {
  [TIERS.FREE]: 0,
  [TIERS.PLUS]: 1,
  [TIERS.PRO]: 2,
  [TIERS.EVOK]: 3,
  [TIERS.EVOK_ULTRA]: 4,
  // High-level tiers that should have access to everything
  [TIERS.BETA]: 99,
  [TIERS.ADMIN]: 100,
  [TIERS.PRO_SECURE]: 100,
  [TIERS.E2EE]: 100,
};

export const PaidGate = ({
  children,
  requiredTier = TIERS.PRO, // Default required tier
  feature, // e.g., "Multiple Workspaces"
  info, // e.g., "Upgrade to create more workspaces."
  isLocked = false, // New prop to force the gate to be locked
  lockMessage, // New prop for custom lock message, e.g., "Limit Reached"
  className = '',
  showUpgradeButton = false,
  initialOverlayOpacity = 0.0,
  hoverOverlayOpacity = 0.60,
  buttonTopMargin = 0
}) => {
  const { isLightTheme } = useTheme();
  const userTier = authManager.getTier();

  const hasAccess = () => {
    // If the gate is explicitly locked by a parent component, access is always denied.
    if (isLocked) return false;

    const userLevel = TIER_HIERARCHY[userTier] ?? -1;
    const requiredLevel = TIER_HIERARCHY[requiredTier] ?? 999;
    
    // User has access if their tier level is greater than or equal to the required level.
    return userLevel >= requiredLevel;
  };

  if (hasAccess()) {
    return children;
  }

  // Determine the message to show on the lock.
  const getMessage = () => {
    if (isLocked) return lockMessage || 'Limit Reached';
    if (requiredTier === TIERS.PRO) return 'Pro Required';
    if (requiredTier === TIERS.EVOK) return 'Evōk Required';
    if (requiredTier === TIERS.EVOK_ULTRA) return 'Evōk Ultra Required';
    return `${requiredTier} Required`;
  };

  // Animation variants with dynamic opacities
  const overlayVariants = {
    initial: { opacity: initialOverlayOpacity },
    hover: { opacity: hoverOverlayOpacity }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Original Content - Dimmed */}
      <div className="opacity-40 h-full">
        {children}
      </div>

      {/* Semi-transparent Overlay */}
      <motion.div
        initial="initial"
        whileHover="hover"
        variants={overlayVariants}
        className={`absolute inset-0 z-10
                   ${isLightTheme ? 'bg-white' : 'bg-surface'}
                   backdrop-blur-[1px] rounded-lg
                   flex items-center justify-center
                   cursor-not-allowed`}
        style={{ paddingTop: buttonTopMargin }}
      >
        <Tooltip.Provider delayDuration={300}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div className={`flex items-center gap-2 px-3 py-1.5
                            rounded-full border border-primary
                            ${isLightTheme ? 'bg-[#1F2937]' : 'bg-[#0F172A]'}`}>
                <Lock className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-bold text-primary">
                  {getMessage()}
                </span>
              </div>
            </Tooltip.Trigger>

            {info && (
              <Tooltip.Portal>
                <Tooltip.Content
                  className={`z-[9999] max-w-xs p-3
                            border border-border-color rounded-lg
                            shadow-lg text-sm
                            ${isLightTheme ? 'bg-white' : 'bg-surface-dark'}`}
                  sideOffset={5}
                >
                  <div className="space-y-2">
                    <p className="text-on-surface/80">{info}</p>
                    {showUpgradeButton && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Add your upgrade navigation logic here
                        }}
                        className={`w-full px-3 py-1.5 text-xs text-primary
                                 hover:text-primary-light border border-primary/20
                                 rounded-lg hover:bg-primary/5 transition-colors
                                 ${isLightTheme ? 'bg-[#1F2937]' : 'bg-[#0F172A]'}`}
                      >
                        Upgrade Plan
                      </button>
                    )}
                  </div>
                  <Tooltip.Arrow className={isLightTheme ? 'fill-white' : 'fill-surface-dark'} />
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>
        </Tooltip.Provider>
      </motion.div>
    </div>
  );
};

export default PaidGate;